// components/Admin/SellerManagement/AdminSpecialSetCreator.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toBlob } from 'html-to-image';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../../../../components/ui/card";
import { Slider } from "../../../../components/ui/slider";
import { Label } from "../../../../components/ui/label";
import { Button } from "../../../../components/ui/button";
import { useToast } from "../../../../hooks/use-toast";
import { getSellerSpecialSetAnalytics, adminCreateSpecialSet, getAllSpecialSetTemplates }from '../../../../services/AdminSpecialSetApis';
import { Loader2, Zap, Info, CircleChevronRight, Coins, Copy } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Input } from "../../../../components/ui/input";
import { usePermissions } from '../../../../hooks/usePermissions';

const StatCard = ({ title, value }) => (
    <div className="p-4 bg-gray-100 rounded-lg text-center">
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-xl font-bold">{value}</p>
    </div>
);

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
};

// --- Shareable summary card rendered off-screen and captured as an image ---
const SpecialSetShareCard = ({ leads, priceDetails, discount }) => (
    <div className="w-full overflow-hidden rounded-xl border-2 border-brand-gold-accent bg-white shadow-sm">
        {/* Header band */}
        <div className="bg-brand-deep-navy px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-gold-accent">
                Special Set Offer
            </p>
            <h3 className="text-lg font-bold leading-snug text-white">
                {leads} Leads Package
            </h3>
        </div>

        <div className="space-y-4 p-5">
            {/* Leads & coins highlight */}
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-gray-50 p-3 text-center">
                    <p className="text-xs text-muted-foreground">Approx. Leads</p>
                    <p className="text-2xl font-extrabold tabular-nums text-brand-deep-navy">{leads}</p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 text-center">
                    <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <Coins className="h-3.5 w-3.5 text-brand-gold-accent" /> Coins
                    </p>
                    <p className="text-2xl font-extrabold tabular-nums text-brand-deep-navy">
                        {priceDetails?.total_coins || 0}
                    </p>
                </div>
            </div>

            {/* Price breakdown */}
            <div className="space-y-1.5 text-sm">
                {discount > 0 && (
                    <div className="flex items-baseline justify-between font-semibold text-brand-growth-green">
                        <span>Discount Applied</span>
                        <span className="tabular-nums">{discount}% OFF</span>
                    </div>
                )}
                <div className="flex items-baseline justify-between">
                    <span className="text-muted-foreground">Subtotal (ex. GST)</span>
                    <span className="tabular-nums">{formatCurrency(priceDetails?.subtotal)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span className="tabular-nums">+ {formatCurrency(priceDetails?.tax_amount)}</span>
                </div>
            </div>

            {/* Total */}
            <div className="rounded-lg bg-gradient-to-b from-amber-50 to-white p-4">
                <div className="flex items-end justify-between gap-3">
                    <span className="text-sm font-medium text-brand-deep-navy">Total Price (inc. GST)</span>
                    <span className="text-2xl font-extrabold tabular-nums text-brand-deep-navy">
                        {formatCurrency(priceDetails?.total_payable)}
                    </span>
                </div>
            </div>
        </div>
    </div>
);

const AdminSpecialSetCreator = ({ sellerProfileId }) => {
    const [analytics, setAnalytics] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const { toast } = useToast();
    const [isOverrideEnabled, setIsOverrideEnabled] = useState(false);
    const [manualDiscount, setManualDiscount] = useState(10);
    const { hasPermission } = usePermissions();

    const [selectedLeads, setSelectedLeads] = useState(50);
    const [isCopying, setIsCopying] = useState(false);
    const shareCardRef = useRef(null);

    const canConfigureByCoins = hasPermission("sales:special-set-coin-config");
    const [inputMode, setInputMode] = useState('leads'); // 'leads' | 'coins'
    const [manualCoins, setManualCoins] = useState(500);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const [analyticsData, templatesData] = await Promise.all([
                    getSellerSpecialSetAnalytics(sellerProfileId),
                    getAllSpecialSetTemplates()
                ]);
                console.log("Analystics Data:", analyticsData);
                setAnalytics(analyticsData);
                setTemplates(templatesData);
            } catch (error) {
                toast({ variant: "destructive", title: "Error", description: "Could not load seller data." });
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [sellerProfileId, toast]);

    const { tiers, minLeads, maxLeads } = useMemo(() => {
        if (!templates || templates.length === 0) return { tiers: [], minLeads: 10, maxLeads: 200 };
        const sortedTemplates = [...templates].sort((a, b) => a.level.localeCompare(b.level));
        const builtTiers = sortedTemplates.map((template, i) => {
            const min = template.min_leads;
            const max = (i + 1 < sortedTemplates.length) ? sortedTemplates[i+1].min_leads - 1 : template.max_leads;
            return { min_leads: min, max_leads: max, discount_percentage: template.discount_percentage };
        });
        return { tiers: builtTiers, minLeads: builtTiers[0]?.min_leads || 10, maxLeads: builtTiers[builtTiers.length - 1]?.max_leads || 200 };
    }, [templates]);

    const equivalentLeads = useMemo(() => {
        if (!analytics?.avg_coins_per_lead) return 0;
        return Math.round(manualCoins / analytics.avg_coins_per_lead);
    }, [manualCoins, analytics]);

    const { priceDetails, activeDiscount } = useMemo(() => {
        if (!analytics || tiers.length === 0) return { priceDetails: null, activeDiscount: 0 };
        const isCoinsMode = inputMode === 'coins' && canConfigureByCoins;
        const leadsForTierLookup = isCoinsMode ? equivalentLeads : selectedLeads;
        const tieredDiscount = tiers.find(tier => leadsForTierLookup >= tier.min_leads && leadsForTierLookup <= tier.max_leads)?.discount_percentage || 0;
        const discount = isOverrideEnabled ? manualDiscount : tieredDiscount;
        const total_coins = isCoinsMode ? manualCoins : Math.round(analytics.avg_coins_per_lead * selectedLeads);
        const base_coin_rate = 35;
        const tax_rate_percentage = 18;
        const price_before_discount = total_coins * base_coin_rate;
        const discount_amount = (price_before_discount * discount) / 100;
        const subtotal = price_before_discount - discount_amount;
        const tax_amount = subtotal * (tax_rate_percentage / 100);
        const total_payable = subtotal + tax_amount;
        return { priceDetails: { total_coins, subtotal, tax_amount, total_payable }, activeDiscount: discount };
    }, [analytics, tiers, selectedLeads, isOverrideEnabled, manualDiscount, inputMode, canConfigureByCoins, manualCoins, equivalentLeads]);

    const displayLeads = (inputMode === 'coins' && canConfigureByCoins) ? equivalentLeads : selectedLeads;

    const handleSaveSet = async () => {
        const isCoinsMode = inputMode === 'coins' && canConfigureByCoins;
        setIsSaving(true);
        try {
            const newPackage = await adminCreateSpecialSet(sellerProfileId, {
                number_of_leads: isCoinsMode ? equivalentLeads : selectedLeads,
                discount_percentage: activeDiscount,
                total_coins: isCoinsMode ? manualCoins : undefined,
            });
            toast({ title: "Success!", description: "Special set has been created for the seller." });
            // Refresh analytics to show the new package
            const data = await getSellerSpecialSetAnalytics(sellerProfileId);
            setAnalytics(data);
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "Failed to create special set." });
        } finally {
            setIsSaving(false);
        }
    };

    const copySummaryAsImage = async () => {
        if (!shareCardRef.current) return;

        if (!navigator.clipboard?.write || !window.ClipboardItem) {
            toast({
                variant: "destructive",
                title: "Copy Not Supported",
                description: "This browser does not support copying images to the clipboard.",
            });
            return;
        }

        try {
            setIsCopying(true);

            const captureNode = shareCardRef.current;
            const captureWidth = Math.ceil(captureNode.scrollWidth);
            const captureHeight = Math.ceil(captureNode.scrollHeight) + 40;

            const imageBlob = await toBlob(captureNode, {
                backgroundColor: '#ffffff',
                width: captureWidth,
                height: captureHeight,
                pixelRatio: 2,
                cacheBust: true,
                style: {
                    position: 'static',
                    left: '0',
                    top: '0',
                    overflow: 'visible',
                },
            }).then((blob) => {
                if (!blob) {
                    throw new Error('Could not create an image of the special set summary.');
                }
                return blob;
            });

            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': imageBlob }),
            ]);

            toast({
                title: "Copied as Image",
                description: "The special set summary is ready to paste from your clipboard.",
            });
        } catch (copyError) {
            console.error('Could not copy special set summary as an image:', copyError);
            toast({
                variant: "destructive",
                title: "Copy Failed",
                description: "Could not copy the summary. Use HTTPS or localhost and try again.",
            });
        } finally {
            setIsCopying(false);
        }
    };

    const handleManualDiscountChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (isNaN(value)) {
            setManualDiscount(0); // Reset to 0 if input is not a number
        } else {
            setManualDiscount(Math.max(0, Math.min(value, 50))); // Clamp between 0 and 50
        }
    };

    if (isLoading) {
        return <div className="flex justify-center items-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    }

    return (
        <div className="p-4 md:p-6">
            {/* Off-screen node captured by "Copy as Image" */}
            <div
                ref={shareCardRef}
                aria-hidden="true"
                className="fixed top-0 bg-white p-2"
                style={{ left: '-10000px', width: '360px', pointerEvents: 'none' }}
            >
                <SpecialSetShareCard
                    leads={displayLeads}
                    priceDetails={priceDetails}
                    discount={activeDiscount}
                />
            </div>
            <Card className="bg-white shadow-lg border border-gray-200">
                <CardHeader>
                    <CardTitle>Special Set Calculator</CardTitle>
                    <CardDescription>
                        Use this tool to calculate special sets.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
    
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Seller Analytics</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <StatCard title="Leads (Last 7d)" value={analytics.lead_purchase_count_7d} />
                            <StatCard title="Leads (Last 30d)" value={analytics.lead_purchase_count_30d} />
                            <StatCard title="Avg. Coins / Lead" value={parseFloat(analytics.avg_coins_per_lead).toFixed(2)} />
                            <StatCard title="Current Level" value={analytics.current_qualification_level.replace('_', ' ')} />
                        </div>

                        {/* --- NEW: Display Existing Set Info --- */}
                        {analytics?.existing_special_set?.length > 0 && (
                            <Alert variant="default">
                                <Info className="h-4 w-4" />
                                <AlertTitle>Existing Special Sets Active</AlertTitle>
                                <AlertDescription>
                                {analytics.existing_special_set.map((set) => (
                                <div key={set.id} className="mb-4 pl-6 relative">
                                    <CircleChevronRight className="absolute left-0 top-1.5 h-5 w-5 text-muted-foreground" />
                                    
                                    <div>
                                    <span className="font-semibold">{set.package_name}</span> —{" "}
                                    <span className="font-semibold">{set.calculated_set_coins} coins</span> for approx.{" "}
                                    <span className="font-semibold">{set.leads} leads</span>, at a{" "}
                                    <span className="font-semibold">{set.discount_percentage}% discount</span>.
                                    </div>

                                    <div>
                                    Set Cost ₹{" "}
                                    <span className="font-semibold">
                                        {Number(set.calculated_set_price_after_discount_ex_tax).toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                        })}
                                    </span>{" "}
                                    ex. GST. (
                                    <span className="font-semibold">
                                        ₹{Number(set.calculated_set_price_after_tax).toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                        })}
                                    </span>{" "}
                                    inc. 18% GST)
                                    </div>

                                    <div>
                                    Created by <span className="font-semibold">{set.created_by}</span>
                                    {set.admin_email && (
                                        <> (<span className="font-semibold">{set.admin_email}</span>)</>
                                    )}.
                                    </div>
                                </div>
                                ))}

                                Creating a new Special Set will replace the existing ones.
                                </AlertDescription>
                            </Alert>
                            )}

                        <Card>
                            <CardHeader>
                                <CardTitle>Create Special Set (Admin)</CardTitle>
                                <CardDescription>Manually create a special coin package for this seller.</CardDescription>
                            </CardHeader>
                            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    {canConfigureByCoins && (
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant={inputMode === 'leads' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setInputMode('leads')}
                                                disabled={isSaving}
                                            >
                                                By Leads
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={inputMode === 'coins' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setInputMode('coins')}
                                                disabled={isSaving}
                                            >
                                                By Coins
                                            </Button>
                                        </div>
                                    )}
                                    {inputMode === 'coins' && canConfigureByCoins ? (
                                        <div>
                                            <Label htmlFor="manual-coins">
                                                Number of Coins: <span className="font-bold text-blue-600">{manualCoins}</span>
                                                {" "}(~{equivalentLeads} leads)
                                            </Label>
                                            <Input
                                                id="manual-coins"
                                                type="number"
                                                value={manualCoins}
                                                onChange={(e) => setManualCoins(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                                min={1}
                                                placeholder={`e.g. ${Math.round(minLeads * (analytics?.avg_coins_per_lead || 0))} - ${Math.round(maxLeads * (analytics?.avg_coins_per_lead || 0))}`}
                                                disabled={isSaving}
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <Label>Number of Leads: <span className="font-bold text-blue-600">{selectedLeads}</span></Label>
                                            <Slider
                                                min={minLeads}
                                                max={maxLeads}
                                                step={1}
                                                value={[selectedLeads]}
                                                onValueChange={(val) => setSelectedLeads(val[0])}
                                                disabled={isSaving}
                                            />
                                        </div>
                                    )}
                                    {/* NEW: Manual Discount Override Section */}
                                    {hasPermission("sales:custom-discount-special-set") &&<div className="space-y-4 rounded-md border p-4">
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="override-discount"
                                                checked={isOverrideEnabled}
                                                onCheckedChange={setIsOverrideEnabled}
                                                disabled={isSaving}
                                            />
                                            <Label htmlFor="override-discount" className="font-semibold">
                                                Override Discount Manually
                                            </Label>
                                        </div>
                                        {isOverrideEnabled && (
                                            <div className="space-y-1 pl-6">
                                                <Label htmlFor="manual-discount">Custom Discount (%)</Label>
                                                <Input
                                                    id="manual-discount"
                                                    type="number"
                                                    value={manualDiscount}
                                                    onChange={handleManualDiscountChange}
                                                    max={50}
                                                    min={0}
                                                    disabled={isSaving}
                                                    className="max-w-[120px]"
                                                />
                                                <p className="text-xs text-muted-foreground">Enter a value between 0 and 50.</p>
                                            </div>
                                        )}
                                    </div>}
                                    <div className={`space-y-4 transition-opacity ${isOverrideEnabled ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                                        <Label>Discount Tiers (Automatic)</Label>
                                        <div className="space-y-2 mt-2">
                                            {tiers.map(tier => (
                                                <Alert key={tier.min_leads} variant={displayLeads >= tier.min_leads && displayLeads <= tier.max_leads ? "default" : "secondary"}>
                                                    <Zap className="h-4 w-4" />
                                                    <AlertTitle className="font-bold">{tier.discount_percentage}% Discount</AlertTitle>
                                                    <AlertDescription>
                                                        For {tier.min_leads} to {tier.max_leads} leads.
                                                    </AlertDescription>
                                                </Alert>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {/* MODIFIED: Package Summary now uses `text-primary` for consistency */}
                                <div className="bg-muted/40 p-6 rounded-lg space-y-3">
                                    <h3 className="font-semibold text-lg">Package Summary</h3>
                                    <div className="flex justify-between items-center text-2xl font-bold text-primary">
                                        <span>{displayLeads}</span>
                                        <span>Leads{inputMode === 'coins' && canConfigureByCoins ? ' (approx.)' : ''}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xl font-bold text-primary">
                                        <span>{priceDetails?.total_coins || 0}</span>
                                        <span>Coins</span>
                                    </div>
                                    <hr/>
                                    <div className="space-y-2 text-sm">
                                        <div className={`flex justify-between font-semibold ${activeDiscount > 0 ? 'text-brand-growth-green' : ''}`}>
                                            <span>Discount Applied</span>
                                            <span>{activeDiscount}% OFF</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Subtotal</span>
                                            <span>{formatCurrency(priceDetails?.subtotal)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">GST (18%)</span>
                                            <span>+ {formatCurrency(priceDetails?.tax_amount)}</span>
                                        </div>
                                        <div className="flex justify-between font-bold text-base mt-2 pt-2 border-t">
                                            <span>Total Price</span>
                                            <span>{formatCurrency(priceDetails?.total_payable)}</span>
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={copySummaryAsImage}
                                        disabled={isCopying}
                                        className="w-full mt-4"
                                    >
                                        {isCopying ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <Copy className="mr-2 h-4 w-4" />
                                        )}
                                        {isCopying ? 'Copying...' : 'Copy as Image'}
                                    </Button>
                                    {hasPermission("sales:create-special-set") &&<Button onClick={handleSaveSet} disabled={isSaving} className="w-full mt-4">
                                        {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        {isSaving ? 'Saving...' : 'Save as a Set'}
                                    </Button>}
                                </div>
                            </CardContent>
                            <CardFooter>
                            <div className="text-xs text-muted-foreground space-y-1 mt-4">
                            <p className="font-semibold">Terms & Conditions:</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>There is no limit for purchasing the Special Set offers.</li>
                                <li>The Special Set will expire automatically after 90 days of creation if the level requirements are not maintained/reached.</li>
                                <li>The saved Special Set's pricing may change if the per coin rates change. This will be notified in advance.</li>
                                <li>The coin expiry is governed by the Terms and Conditions.</li>
                            </ul>
                            </div>
                        </CardFooter>
                        </Card>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminSpecialSetCreator;
